import React, { useEffect, useCallback, useMemo, memo } from 'react';
import { SliderControl } from './SliderControl';
import './ImageTuningStyle.scss';
import {
  deleteDropTuningValues,
  getDefaultTuningWindow,
  getDropTuningValuesVM,
  GetHistogramFromBitmap,
  GetHistogramFromCompositeImage,
  getTuningWindowForDrop,
  getTuningWindowForPlateAuto,
  histogramSmoothing,
  resetExperimentTuningValues,
  saveExperimentTuningValues,
  updateTuningValuesForPlate,
} from './ImageTuningManager';
import { GetCaptureProfileTuningValues, isDropWellCompare } from 'utils/Global';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectImageTuningValues } from 'redux/selectors/StateSelectors';
import { setImageTuning } from 'redux/reducers/experimentReducer';
import { IStyle } from 'Styles';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { CustomCheckBox } from 'components/input';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import {
  appendHistogramIdentifiers,
  getCompositeImageStoreImageByKey,
  removeHistogramIdentifiers,
  setExpTuningValues,
} from 'redux/reducers/experimentDataReducer';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
  usePlateImageStructure,
} from 'redux/hooks/experiment';
import {
  useCurrentWellDropImageStatus,
  useShowOverrideTuning,
  useUpdateWellDropImageTuningValues,
} from 'redux/hooks/well';
import { setShowOverrideTuning } from 'redux/reducers/wellReducer';
import { getTuningWindowForPlate } from './utils';
import { cloneDeep } from 'lodash';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { WellDetailsStates } from 'business/models/CustomModels';
import { getRegionIdCached, getWellImageParams } from 'components/well/utils';
import WellDropImageStore from 'components/well/wellDropImageStore';
import { getWellDropImageUniqueKeyByWellDetails } from 'redux/utils/well';
import { saveWellDropTuningValuesThunk } from 'redux/thunks/well';
import { ImageTuningLimitType } from './constants';

function ImageTuningPanel() {
  const experimentData = useRMSelector(selectExp);

  const isDropTab = isDropWellCompare();
  const dispatch = useRMDispatch();
  const plateImageStructures = usePlateImageStructure();
  const compositeImage = getCompositeImageStoreImageByKey(
    plateImageStructures?.find((x) => x.imageCompositeKeyString)?.imageCompositeKeyString,
  );
  const canvasImg = compositeImage == 'NoImage' ? undefined : compositeImage;
  const imageTuningValues = useRMSelector((state) => selectImageTuningValues(state));
  const showOverride = useShowOverrideTuning();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const updateTuningValuesInImageStatusMeta = useUpdateWellDropImageTuningValues();
  const { wellNum, plateNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = selectedDropImageParameters || {};
  const imageBatchId = inspection.groupedInspections?.find(
    (grp) => grp.plateId === plateID,
  )?.imageBatchId;

  const wellDetails = useMemo(() => {
    return {
      wellNum,
      plateNum,
      plateID,
      cpID: cpId,
      dropNumber,
      inspectionSet: inspection,
      regionName,
      imageBatchId: imageBatchId,
      regionID: getRegionIdCached(
        plateID,
        wellNum,
        dropNumber,
        regionName,
        experimentData.regions,
      ),
    } as WellDetailsStates;
  }, [wellNavigationDetails, selectedDropImageParameters, imageBatchId]);

  const dropImage =
    WellDropImageStore.get(getWellDropImageUniqueKeyByWellDetails(wellDetails))
      ?.original || undefined;

  const _setExpTuningValues = useCallback(
    async (values) => {
      dispatch(setExpTuningValues(values));
    },
    [dispatch],
  );
  const uIKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);
  const currentWellImageStatus = useCurrentWellDropImageStatus(uIKey);
  const isCurrentImageLoaded =
    currentWellImageStatus?.fetchingStatus === EFetchingStatus.Loaded || false;

  const { lowerLevelLimit, upperLevelLimit, isAutoLevelingOn } = useMemo(() => {
    if (isDropTab && isCurrentImageLoaded) {
      return {
        lowerLevelLimit:
          currentWellImageStatus.meta.updatedTuningValues?.lowerLevelLimit ??
          imageTuningValues?.lowerLevelLimit,
        upperLevelLimit:
          currentWellImageStatus.meta.updatedTuningValues?.upperLevelLimit ??
          imageTuningValues?.upperLevelLimit,
        isAutoLevelingOn:
          currentWellImageStatus.meta.updatedTuningValues?.isAutoLevelingOn ??
          imageTuningValues?.isAutoLevelingOn,
      };
    } else {
      return {
        lowerLevelLimit: imageTuningValues?.lowerLevelLimit,
        upperLevelLimit: imageTuningValues?.upperLevelLimit,
        isAutoLevelingOn: imageTuningValues?.isAutoLevelingOn,
      };
    }
  }, [isDropTab, isCurrentImageLoaded, currentWellImageStatus, imageTuningValues]);

  const plateImageCompositeKey = plateImageStructures?.find(
    (p) => p.plateNumber === wellNavigationDetails.plateNum,
  )?.imageCompositeKeyString;
  const plateImage = getCompositeImageStoreImageByKey(plateImageCompositeKey);

  useEffect(() => {
    if (!isDropTab) {
      return;
    } else if (!showOverride) {
      const canvasTuningValues = experimentData.experimentTuningValues.find(
        (tv) => tv.captureProfileID === cpId,
      );
      if (canvasTuningValues) {
        if (canvasTuningValues.isAutoLevelingOn) {
          const autoTuingValues = getTuningWindowForPlateAuto(
            experimentData.experimentTuningValues,
            plateImage,
            cpId,
            true,
          );
          dispatch(setImageTuning(autoTuingValues));
        } else {
          dispatch(setImageTuning(canvasTuningValues));
        }
      } else {
        const imageTuning = getTuningWindowForPlate(undefined, canvasImg, cpId);
        dispatch(setImageTuning(imageTuning));
      }
    }
  }, [showOverride, isDropTab]);

  const dropRegionId = getRegionIdCached(
    plateID,
    wellNum,
    dropNumber,
    regionName,
    experimentData.regions,
  );

  //on mousemove
  function onSliderMove(params) {
    if (!params[0] && !params[1]) {
      return;
    }
    const imageTuning: ImageTuningValues = {};
    imageTuning.lowerLevelLimit = params[0];
    imageTuning.upperLevelLimit = params[1];
    imageTuning.isAutoLevelingOn = false;
    if (isDropTab) {
      updateTuningValuesInImageStatusMeta(uIKey, imageTuning);
    } else {
      dispatch(setImageTuning(imageTuning));
    }
  }

  // On mouseup, fix the slider values so the range is always valid:
  // - keep the upper limit above 0 and the lower limit below 255
  // - make sure lowerLimit is always strictly less than upperLimit
  async function onSliderRelease(params, type: ImageTuningLimitType) {
    let lowerLimit = params[0];
    let upperLimit = params[1];
    if (upperLimit <= 0) {
      upperLimit = 1;
    }
    if (lowerLimit >= 255) {
      lowerLimit = 254;
    }
    if (type === ImageTuningLimitType.Lower) {
      if (lowerLimit >= upperLimit) {
        upperLimit = lowerLimit + 1;
      }
    } else if (ImageTuningLimitType.Upper) {
      if (upperLimit <= lowerLimit) {
        lowerLimit = upperLimit - 1;
      }
    }

    const imageTuning: ImageTuningValues = {};
    imageTuning.lowerLevelLimit = lowerLimit;
    imageTuning.upperLevelLimit = upperLimit;
    imageTuning.isAutoLevelingOn = false;
    if (isDropTab) {
      updateTuningValuesInImageStatusMeta(uIKey, imageTuning);
    } else {
      dispatch(setImageTuning(imageTuning));
    }

    if (!isDropTab) {
      const tuningValues = cloneDeep(experimentData.experimentTuningValues);
      saveExperimentTuningValues(
        lowerLimit,
        upperLimit,
        cpId,
        false,
        experimentData.experimentId,
      ); /* Save tuning values for experiment in Database */
      const updatedValues = updateTuningValuesForPlate(
        tuningValues,
        lowerLimit,
        upperLimit,
        cpId,
        false,
        experimentData.experimentId,
      ); /* Get Updated Object for experiment Tuning values */
      _setExpTuningValues(
        updatedValues,
      ); /* Set Updated experiment tuning values in redux */
    } else {
      /* Save Calculated values for drop in DB */
      dispatch(
        saveWellDropTuningValuesThunk({
          lowerLimit,
          upperLimit,
          captureProfileId: cpId,
          dropRegionId,
          imageBatchId,
          isAutoLevelingOn: false,
          uIKeyParams: getWellImageParams(wellDetails),
        }),
      );
      /* Add histogram details in drawing identifiers */
      appendOrRemoveHistogram(imageTuning.lowerLevelLimit, imageTuning.upperLevelLimit);
    }
  }

  //------------------------------- -------------------------

  function getHistogramValues() {
    let imgData;
    let histogramValues;

    if (isDropTab) {
      imgData = dropImage;
      if (imgData == undefined || imgData == 'NoImage') {
        return;
      }

      histogramValues = GetHistogramFromBitmap(imgData);
    } else {
      imgData = canvasImg;
      if (imgData == undefined) {
        return;
      }

      histogramValues = GetHistogramFromCompositeImage(imgData);
    }

    return histogramValues;
  }

  const histogramMaxHeight = 130;
  const histogramWidth = 525;

  function drawHistogram() {
    let histogramValues = [];
    histogramValues = getHistogramValues();

    if (histogramValues == undefined) {
      return;
    }

    const hBars = [];

    histogramValues = histogramSmoothing(histogramValues);

    let maxHValue = 0;
    for (let i = 0; i < histogramValues.length; i++) {
      if (maxHValue < histogramValues[i]) {
        maxHValue = histogramValues[i];
      }
    }

    const barWidth = histogramWidth / 256;

    for (let i = 0; i < histogramValues.length; i++) {
      const hValue = histogramValues[i];
      const barLength = (hValue / maxHValue) * histogramMaxHeight;

      hBars.push(
        <div
          className="histogramBar"
          style={{
            height: barLength,
            width: barWidth,
          }}
        ></div>,
      );
    }

    return hBars;
  }

  async function onAutoClick() {
    const tuningValues = cloneDeep(experimentData.experimentTuningValues);
    if (!isDropTab) {
      const calculatedValues: ImageTuningValues = getTuningWindowForPlateAuto(
        tuningValues,
        canvasImg,
        cpId,
        !isAutoLevelingOn,
      );
      dispatch(
        setImageTuning(calculatedValues),
      ); /* Update temporary values bound to silder control */
      const expTVIdx = tuningValues.findIndex((t) => t.captureProfileID === cpId);
      if (expTVIdx >= 0) {
        /* Set Auto to true/false in DB and Redux */
        tuningValues[expTVIdx].isAutoLevelingOn = !isAutoLevelingOn;
        saveExperimentTuningValues(
          tuningValues[expTVIdx].lowerLevelLimit,
          tuningValues[expTVIdx].upperLevelLimit,
          cpId,
          !isAutoLevelingOn,
          experimentData.experimentId,
        );
        _setExpTuningValues(tuningValues);
      } else {
        /* Create new tuning Tuning values in DB and Redux */
        saveExperimentTuningValues(
          calculatedValues.lowerLevelLimit,
          calculatedValues.upperLevelLimit,
          cpId,
          !isAutoLevelingOn,
          experimentData.experimentId,
        );
        _setExpTuningValues(tuningValues);
      }
    } else if (showOverride) {
      //drop tab
      const captureResultTuningValuesVM = getDropTuningValuesVM(
        lowerLevelLimit,
        upperLevelLimit,
        cpId,
        dropRegionId,
        !isAutoLevelingOn,
      ); /* Get default capture profile values */
      const calculatedValues = getTuningWindowForDrop(
        tuningValues,
        dropImage,
        cpId,
        captureResultTuningValuesVM,
      ); /* Calculate auto tuning values */
      updateTuningValuesInImageStatusMeta(uIKey, calculatedValues);

      /* Save Calculated values for drop in DB */
      dispatch(
        saveWellDropTuningValuesThunk({
          lowerLimit: calculatedValues.lowerLevelLimit,
          upperLimit: calculatedValues.upperLevelLimit,
          captureProfileId: cpId,
          dropRegionId,
          imageBatchId,
          isAutoLevelingOn: !isAutoLevelingOn,
          uIKeyParams: getWellImageParams(wellDetails),
        }),
      );
      appendOrRemoveHistogram(
        calculatedValues.lowerLevelLimit,
        calculatedValues.upperLevelLimit,
      );
    }
  }

  async function onResetClick() {
    const tuningValues = cloneDeep(experimentData.experimentTuningValues);
    if (!isDropTab) {
      const defaultTuningValues = getDefaultTuningWindow(cpId);
      if (defaultTuningValues.isAutoLevelingOn === isAutoLevelingOn) {
        dispatch(setImageTuning({ ...defaultTuningValues, isAutoLevelingOn: false }));
      } else {
        dispatch(setImageTuning(defaultTuningValues));
      }
      resetExperimentTuningValues(tuningValues, cpId);
      _setExpTuningValues(tuningValues);
    } else {
      //drop tab
      await deleteDropTuningValues(dropRegionId, cpId, imageBatchId);
      const expTuningValue = experimentData.experimentTuningValues.find(
        (t) => t.captureProfileID === cpId,
      );
      if (
        expTuningValue &&
        !(
          expTuningValue.lowerLevelLimit === lowerLevelLimit &&
          expTuningValue.upperLevelLimit === upperLevelLimit
        )
      ) {
        updateTuningValuesInImageStatusMeta(uIKey, null);
        dispatch(
          setImageTuning({
            regionId: dropRegionId,
            lowerLevelLimit: expTuningValue.lowerLevelLimit,
            upperLevelLimit: expTuningValue.upperLevelLimit,
            autoLevelingLowThreshold: expTuningValue.autoLevelingLowThreshold,
            autoLevelingHighThreshold: expTuningValue.autoLevelingHighThreshold,
            isAutoLevelingOn: expTuningValue.isAutoLevelingOn,
          }),
        );
        dispatch(removeHistogramIdentifiers(wellDetails));
      } else {
        const defaultTuningValues = GetCaptureProfileTuningValues()?.find(
          (t) => t.captureProfileID === cpId,
        );
        dispatch(
          setImageTuning({
            regionId: dropRegionId,
            lowerLevelLimit: defaultTuningValues.lowerLevelLimit,
            upperLevelLimit: defaultTuningValues.upperLevelLimit,
            autoLevelingLowThreshold: defaultTuningValues.autoLevelingLowThreshold,
            autoLevelingHighThreshold: defaultTuningValues.autoLevelingHighThreshold,
            isAutoLevelingOn: false,
          }),
        );
        const imageTuning: ImageTuningValues = {
          lowerLevelLimit: defaultTuningValues.lowerLevelLimit,
          upperLevelLimit: defaultTuningValues.upperLevelLimit,
          isAutoLevelingOn: false,
        };
        updateTuningValuesInImageStatusMeta(uIKey, imageTuning);
        /* Save Calculated values for drop in DB */
        dispatch(
          saveWellDropTuningValuesThunk({
            lowerLimit: defaultTuningValues.lowerLevelLimit,
            upperLimit: defaultTuningValues.upperLevelLimit,
            captureProfileId: cpId,
            dropRegionId,
            imageBatchId,
            isAutoLevelingOn: false,
            uIKeyParams: getWellImageParams(wellDetails),
          }),
        );
        appendOrRemoveHistogram(
          defaultTuningValues.lowerLevelLimit,
          defaultTuningValues.upperLevelLimit,
        );
      }
    }
  }

  const appendOrRemoveHistogram = (lowerLevelLimit: number, upperLevelLimit: number) => {
    if (lowerLevelLimit > 0 || upperLevelLimit < 255) {
      dispatch(appendHistogramIdentifiers(wellDetails));
    } else {
      dispatch(removeHistogramIdentifiers(wellDetails));
    }
  };

  function onMinValueChanged(e) {
    const minValue = e.target.value;
    onSliderRelease(
      [minValue === '' ? 0 : Number(minValue), upperLevelLimit],
      ImageTuningLimitType.Lower,
    );
  }

  function onMaxValueChanged(e) {
    const maxValue = e.target.value;
    onSliderRelease(
      [lowerLevelLimit, maxValue === '' ? 0 : Number(maxValue)],
      ImageTuningLimitType.Upper,
    );
  }

  function showControl() {
    if (isDropTab) {
      return 'visible';
    } else {
      if (canvasImg == undefined) {
        return 'hidden';
      } else {
        return 'visible';
      }
    }
  }

  function getAutoClassName() {
    if (isDropTab) {
      return isAutoLevelingOn ? 'disabledDiv' : '';
    }

    return isAutoLevelingOn ? 'disabledDiv' : '';
  }

  function getAutoEnableClass() {
    if (isAutoLevelingOn) {
      return 'advButtonStylePressed';
    }

    return 'advButtonStyle';
  }

  const handleOverrideChange = () => {
    dispatch(setShowOverrideTuning(!showOverride));
    if (isDropTab && !showOverride && currentWellImageStatus?.meta?.updatedTuningValues) {
      dispatch(setImageTuning({ ...currentWellImageStatus?.meta?.updatedTuningValues }));
    }
  };

  const isImageLoaded = () => {
    if (isDropTab) {
      return isCurrentImageLoaded;
    }
    return canvasImg;
  };

  const isAutoDisabled = () => {
    return !showOverride || !isImageLoaded() || isAutoLevelingOn;
  };

  return (
    showControl() === 'visible' && (
      <>
        <div style={styles.mainContainer}>
          <div style={styles.checkBoxWithTextContainer}>
            <CustomCheckBox checked={showOverride} onChange={handleOverrideChange} />
            <div style={styles.textStyles}>Show Override</div>
            <div
              className={`ml-auto text-sm text-amber-600 justify-end ${
                isDropTab && !showOverride ? '' : 'hidden'
              }`}
            >
              &#9888; Enable show override to tune drop image
            </div>
          </div>

          <div
            className={getAutoClassName()}
            style={{
              height: histogramMaxHeight,
              width: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            {drawHistogram()}
          </div>
          <div className={getAutoClassName()} style={styles.sliderContainer}>
            <SliderControl
              disabled={(isDropTab && !showOverride) || !isImageLoaded()}
              lowerLevelLimit={lowerLevelLimit}
              upperLevelLimit={upperLevelLimit}
              setSliderValues={onSliderMove} //onslidermove
              updateSliderValues={onSliderRelease} //onmouseup
            />
          </div>
          <div className="d-flex flex-row">
            <div style={styles.actionContainer} className="d-flex flex-row w-50">
              <input
                name="minValue"
                type="number"
                max="254"
                min="0"
                style={{ width: '49px', height: '26px' }}
                value={String(lowerLevelLimit)}
                onChange={onMinValueChanged}
                className={getAutoClassName()}
                disabled={(isDropTab && !showOverride) || !isImageLoaded()}
              />
              <RMCustomButton
                title="AUTO"
                contentContainerClass={getAutoEnableClass()}
                onClick={onAutoClick}
                isDisabled={isAutoDisabled()}
              />
            </div>
            <div style={styles.actionContainer} className="d-flex flex-row w-50">
              <RMCustomButton
                title="RESET"
                contentContainerClass={getAutoEnableClass()}
                onClick={onResetClick}
                isDisabled={!isImageLoaded()}
              />
              <input
                name="maxValue"
                type="number"
                max="255"
                min="1"
                style={{ width: '49px', height: '26px' }}
                value={String(upperLevelLimit)}
                onChange={onMaxValueChanged}
                className={getAutoClassName()}
                disabled={(isDropTab && !showOverride) || !isImageLoaded()}
              />
            </div>
          </div>
        </div>
      </>
    )
  );
}
const styles: IStyle = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingLeft: '0.5vw',
    paddingRight: '0.5vw',
    paddingTop: '3%',
  },

  checkBoxWithTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  sliderContainer: {
    marginTop: '5%',
    paddingLeft: '2%',
    paddingRight: '2%',
  },
  actionContainer: {
    justifyContent: 'space-between',
    marginLeft: '2%',
    marginRight: '2%',
  },
  textStyles: {
    marginLeft: '1.8%',
    fontWeight: 400,
    fontSize: '14px',
    color: '#333333',
  },
};

export default memo(ImageTuningPanel);
